import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { CaseManagementModel } from '@models/case-management.model';
import { CaseAttachmentModel } from '@models/case-attachment.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { UserSelectors } from '@states/user/user.selector-types';
import { UiButtonComponent } from '@ui';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { CaseAddFileDialogComponent } from '../../../../../../pages/case-management/dialogs/case-add-file-dialog/case-add-file-dialog.component';
import { CaseManagerAccessEventsPreviewComponent } from './previews/access-events/case-manager-access-events-preview.component';
import { CaseManagerClipsPreviewComponent } from './previews/clips/case-manager-clips-preview.component';
import { CaseManagerFilesPreviewComponent } from './previews/files/case-manager-files-preview.component';
import { CaseManagerObjectsPreviewComponent } from './previews/objects/case-manager-objects-preview.component';

@Component({
  selector: 'app-case-manager-feature-result',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    UiButtonComponent,
    CaseManagerAccessEventsPreviewComponent,
    CaseManagerClipsPreviewComponent,
    CaseManagerFilesPreviewComponent,
    CaseManagerObjectsPreviewComponent,
  ],
  templateUrl: './case-manager-feature-result.component.html',
  styleUrls: ['./case-manager-feature-result.component.scss'],
})
export class CaseManagerFeatureResultComponent {
  private readonly store$ = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resultsService = inject(IntelligenceResultsService);

  private static readonly ADD_FILE_DIALOG_CONFIG = {
    width: '600px',
    panelClass: 'modal-no-padding',
    height: '700px',
    disableClose: true,
  } as const;

  /**
   * Prompt that "Add clips"/"Add objects" send to the AI. The agent renders the
   * archive list, where the user adds any archive to any case via its "Add to case"
   * action. Tunable if the agent needs different phrasing to surface archives.
   */
  private static readonly SHOW_ARCHIVES_PROMPT = 'Show me all archives';

  private readonly filesPreview = viewChild(CaseManagerFilesPreviewComponent);

  readonly data = input<IntelligenceModels.CaseManager.FeatureResponse | null>(null);
  protected readonly currentUser = this.store$.selectSignal(UserSelectors.selectUser);
  protected readonly canDownload = computed(() => {
    const permission = this.data()?.userPermission;
    return (
      permission !== null &&
      permission !== undefined &&
      permission >= CaseManagementModel.CaseUserPermission.VIEW_DOWNLOAD
    );
  });
  protected readonly canEdit = computed(() => {
    const permission = this.data()?.userPermission;
    return (
      permission !== null &&
      permission !== undefined &&
      permission >= CaseManagementModel.CaseUserPermission.EDIT
    );
  });

  protected addFile(): void {
    const featureData = this.data();
    const currentUserId = this.currentUser()?._id;
    if (!featureData || !currentUserId || !this.canEdit()) {
      return;
    }

    this.dialog
      .open(CaseAddFileDialogComponent, {
        ...CaseManagerFeatureResultComponent.ADD_FILE_DIALOG_CONFIG,
        data: {
          caseId: featureData.caseId,
          currentUserId,
          canEdit: true,
        },
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((changed?: boolean) => {
        if (changed) {
          this.filesPreview()?.reload();
        }
      });
  }

  /**
   * Open the file's edit/view dialog directly (no route sync), reusing the same dialog
   * as "Add file". Reloads the files preview when the dialog reports a change.
   */
  protected editAttachment(attachment: CaseAttachmentModel.CaseAttachmentDocument): void {
    const featureData = this.data();
    const currentUserId = this.currentUser()?._id;
    if (!featureData || !currentUserId) {
      return;
    }

    this.dialog
      .open(CaseAddFileDialogComponent, {
        ...CaseManagerFeatureResultComponent.ADD_FILE_DIALOG_CONFIG,
        data: {
          caseId: featureData.caseId,
          currentUserId,
          canEdit: this.canEdit(),
          attachmentId: attachment._id,
        },
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((changed?: boolean) => {
        if (changed) {
          this.filesPreview()?.reload();
        }
      });
  }

  /**
   * Add clips/objects: pivot to the AI archive list. Closes this case overlay and asks
   * the AI to show all archives; the user then adds any archive to any case from there.
   */
  protected browseArchives(): void {
    if (!this.canEdit()) return;
    this.resultsService.clearOverlay();
    this.resultsService.requestPrompt(CaseManagerFeatureResultComponent.SHOW_ARCHIVES_PROMPT);
  }

  protected featureLabelKey(feature: IntelligenceModels.CaseManager.FeatureType): string {
    switch (feature) {
      case 'clips':
        return 'CASES.VIEW.CLIPS';
      case 'objects':
        return 'CASES.VIEW.OBJECTS';
      case 'attachments':
        return 'CASES.VIEW.EXTERNAL_FILES';
      case 'accessControlEvents':
        return 'CASES.VIEW.ACCESS_CONTROL_EVENTS';
    }
  }
}
